import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const PeopleList = () => {
  const [people, setPeople] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    department_id: '',
    company_id: '',
    created_by: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [peopleResponse, departmentsResponse, companiesResponse] = await Promise.all([
        apiService.people.getAll(),
        apiService.departments.getAll(),
        apiService.companies.getAll()
      ]);
      setPeople(peopleResponse.data);
      setDepartments(departmentsResponse.data);
      setCompanies(companiesResponse.data);
      // Auto-select first company in form data if available
      if (companiesResponse.data.length > 0 && !formData.company_id) {
        setFormData(prev => ({ ...prev, company_id: companiesResponse.data[0].id.toString() }));
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        company_id: parseInt(formData.company_id)
      };

      if (editingPerson) {
        await apiService.people.update(editingPerson.id, {
          ...dataToSubmit,
          updated_by: 1
        });
      } else {
        await apiService.people.create(dataToSubmit);
      }
      
      setShowForm(false);
      setEditingPerson(null);
      // Auto-select first company if available
      const firstCompanyId = companies.length > 0 ? companies[0].id.toString() : '';
      setFormData({ name: '', department_id: '', company_id: firstCompanyId, created_by: 1 });
      fetchData();
    } catch (err) {
      setError('Failed to save person');
      console.error('Error saving person:', err);
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setFormData({
      name: person.name,
      department_id: person.department_id ? person.department_id.toString() : '',
      company_id: person.company_id.toString(),
      created_by: person.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this person?')) {
      try {
        await apiService.people.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete person');
        console.error('Error deleting person:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPerson(null);
    // Auto-select first company if available
    const firstCompanyId = companies.length > 0 ? companies[0].id.toString() : '';
    setFormData({ name: '', department_id: '', company_id: firstCompanyId, created_by: 1 });
  };

  const getDepartmentName = (deptId) => {
    if (!deptId) return 'No Department';
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'Unknown';
  };

  const getCompanyName = (compId) => {
    const comp = companies.find(c => c.id === compId);
    return comp ? comp.name : 'Unknown';
  };

  if (loading) return <div className="loading">Loading people...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">People</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add Person
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingPerson ? 'Edit Person' : 'Add New Person'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company *</label>
              <select
                className="form-control"
                value={formData.company_id}
                onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                required
              >
                {companies.length === 0 ? (
                  <option value="">No companies available</option>
                ) : (
                  companies.map(comp => (
                    <option key={comp.id} value={comp.id}>
                      {comp.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Department (Optional)</label>
              <select
                className="form-control"
                value={formData.department_id}
                onChange={(e) => setFormData({...formData, department_id: e.target.value})}
              >
                <option value="">Select a department</option>
                {departments
                  .filter(dept => dept.company_id.toString() === formData.company_id)
                  .map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingPerson ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Department</th>
              <th>Company</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {people.map(person => (
              <tr key={person.id}>
                <td>{person.id}</td>
                <td>{person.name}</td>
                <td>{getDepartmentName(person.department_id)}</td>
                <td>{getCompanyName(person.company_id)}</td>
                <td>{new Date(person.created_at).toLocaleDateString()}</td>
                <td>
                  <button 
                    className="btn btn-secondary btn-small" 
                    onClick={() => handleEdit(person)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small" 
                    onClick={() => handleDelete(person.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {people.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No people found. Add someone to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default PeopleList;